/**
 * History's compact month calendar (docs/history-ux.md §1): a heat-coloured mini grid (green/red
 * intensity by the day's net), with its own ‹ › month stepper that doesn't touch the range.
 * Tapping a day with settlements selects it (filters the bets list, highlights the chart); tapping
 * it again clears it. Days are UTC, like the API's day groups.
 */
import { useMemo } from "react";
import { Skeleton } from "../ui.js";
import { addMonths, fmtSignedAmt, heatLevel, maxAbsNet, monthWeeks, type Book, type GroupLike } from "../../lib/history.js";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const ALPHA = [0, 16, 30, 48, 70];

function heatStyle(level: number): React.CSSProperties | undefined {
  if (level === 0) return undefined;
  const c = level > 0 ? "var(--color-up)" : "var(--color-down)";
  return { backgroundColor: `color-mix(in srgb, ${c} ${ALPHA[Math.abs(level)]}%, transparent)` };
}

export function MiniCalendar({
  month,
  onMonthChange,
  groups,
  loading,
  error,
  ccy,
  today,
  selectedDay,
  onSelectDay,
  monthNet,
}: {
  /** Any ISO day in the shown month (the 1st by convention). */
  month: string;
  onMonthChange: (month: string) => void;
  groups: GroupLike[];
  loading: boolean;
  error?: boolean;
  ccy: Book;
  /** Today's ISO day (UTC). */
  today: string;
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  monthNet: number | null;
}) {
  const weeks = useMemo(() => monthWeeks(month), [month]);
  const byDay = useMemo(() => new Map(groups.map((g) => [g.key, g])), [groups]);
  const max = useMemo(() => maxAbsNet(groups), [groups]);
  const title = new Date(`${month.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString(undefined, {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  });
  const atCurrent = month.slice(0, 7) >= today.slice(0, 7);

  return (
    <div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMonthChange(addMonths(month, -1))}
          aria-label="Previous month"
          className="grid h-11 w-11 place-items-center rounded-full text-dim hover:bg-raised hover:text-ink lg:h-8 lg:w-8"
        >
          ‹
        </button>
        <div className="min-w-0 flex-1 text-center">
          <div className="text-[13px] font-medium text-ink">{title}</div>
          <div className="nums text-[11px]">
            {loading ? (
              <Skeleton className="mx-auto mt-0.5 h-3 w-14" />
            ) : monthNet == null ? (
              <span className="text-faint">—</span>
            ) : (
              <span className={monthNet >= 0.005 ? "text-up" : monthNet <= -0.005 ? "text-down" : "text-faint"}>{fmtSignedAmt(monthNet, ccy)}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onMonthChange(addMonths(month, 1))}
          disabled={atCurrent}
          aria-label="Next month"
          className="grid h-11 w-11 place-items-center rounded-full text-dim hover:bg-raised hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent lg:h-8 lg:w-8"
        >
          ›
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wide text-faint" aria-hidden>
        {WEEKDAYS.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="mt-1 flex flex-col gap-1" role="grid" aria-label={`${title} P&L by day`}>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1" role="row">
            {week.map((cell, di) => {
              if (!cell) return <div key={di} role="gridcell" />;
              if (loading) return <Skeleton key={di} className="h-11 rounded-md lg:h-9" />;
              const g = byDay.get(cell.day);
              const net = g?.net_pl ?? 0;
              const level = heatLevel(g ? net : null, max);
              const has = !!g && ((g.settled_count ?? 0) > 0 || Math.abs(net) >= 0.005);
              const selected = selectedDay === cell.day;
              const future = cell.day > today;
              const label = `${new Date(`${cell.day}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", month: "long", day: "numeric" })}${
                has ? `: ${fmtSignedAmt(net, ccy)}` : ", nothing settled"
              }`;
              return (
                <div key={di} role="gridcell">
                  <button
                    type="button"
                    disabled={!has && !selected}
                    onClick={() => onSelectDay(selected ? null : cell.day)}
                    aria-pressed={selected}
                    aria-label={label}
                    title={label}
                    style={heatStyle(level)}
                    className={`relative flex h-11 w-full items-center justify-center rounded-md border text-[12px] transition-colors duration-150 motion-reduce:transition-none lg:h-9 ${
                      selected
                        ? "border-amber text-ink ring-1 ring-amber"
                        : has
                          ? "border-transparent text-ink hover:border-border-2"
                          : "border-transparent text-faint"
                    } ${future ? "opacity-40" : ""} ${error ? "opacity-50" : ""}`}
                  >
                    <span className={`nums ${Math.abs(level) >= 3 ? "font-semibold" : ""}`}>{cell.date}</span>
                    {cell.day === today && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-amber" aria-hidden />}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-center gap-1 text-[10px] text-faint" aria-hidden>
        <span className="mr-1">loss</span>
        {[-4, -3, -2, -1].map((l) => (
          <span key={l} className="h-2.5 w-2.5 rounded-sm" style={heatStyle(l)} />
        ))}
        <span className="h-2.5 w-2.5 rounded-sm border border-border-2" />
        {[1, 2, 3, 4].map((l) => (
          <span key={l} className="h-2.5 w-2.5 rounded-sm" style={heatStyle(l)} />
        ))}
        <span className="ml-1">gain</span>
        <span className="ml-2">· UTC days</span>
      </div>
    </div>
  );
}
